import * as React from "react";

import { siteConfig } from "@/config/site";
import { Icons } from "@/components/icons";
import Link from "next/link";
import Image from "next/image";

interface SiteFooterProps extends React.HTMLAttributes<HTMLElement> {
  simpleFooter?: boolean;
}

export function SiteFooter({ simpleFooter, className }: SiteFooterProps) {
  return (
    <footer className="relative overflow-hidden border-t border-slate-200/40 bg-transparent z-50">
      {/* Match marketing background — radial fade + grid */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-50/80 via-transparent to-transparent" />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "radial-gradient(circle, #6366f1 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />

      {/* Ambient orbs — same palette as marketing page */}
      <div className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-indigo-100/30 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -right-20 h-48 w-48 rounded-full bg-violet-100/20 blur-3xl" />

      <div className="container relative mx-auto px-6 lg:px-8 pt-14 pb-8">
        {!simpleFooter && (
          <>
            {/* Top row: brand + columns */}
            <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-5">
              {/* Brand column */}
              <div className="lg:col-span-2 space-y-4">
                <Link href="/" className="inline-flex items-center gap-2 group">
                  <Image
                    src="/botbyte-logo.png"
                    alt={siteConfig.name}
                    width={250}
                    height={120}
                    className="h-10 w-auto object-contain transition-transform group-hover:scale-[1.02] sm:h-11 md:h-12"
                    priority
                  />
                </Link>
                <p className="max-w-xs text-sm leading-relaxed text-slate-500">
                  Build smarter AI chatbots powered by Claude, GPT & Gemini.
                  Embed on any website in minutes.
                </p>
                {/* Social icons */}
                <div className="flex items-center gap-3 pt-1">
                  <Link
                    href={siteConfig.links.github}
                    target="_blank"
                    prefetch={false}
                    className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-500 transition-all duration-200 hover:bg-slate-900 hover:text-white hover:shadow-md"
                  >
                    <Icons.gitHub className="h-4 w-4" />
                  </Link>
                  <Link
                    href={siteConfig.links.twitter}
                    target="_blank"
                    prefetch={false}
                    className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-500 transition-all duration-200 hover:bg-sky-500 hover:text-white hover:shadow-md"
                  >
                    <Icons.twitter className="h-4 w-4" />
                  </Link>
                  <Link
                    href={siteConfig.links.hashnode}
                    target="_blank"
                    prefetch={false}
                    className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-500 transition-all duration-200 hover:bg-indigo-500 hover:text-white hover:shadow-md"
                  >
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M2.61 8.42 8.42 2.61a3.49 3.49 0 0 1 4.94 0l2.21 2.21a.37.37 0 0 1 0 .53l-6.83 6.83a.37.37 0 0 1-.53 0L6.01 9.97a.37.37 0 0 1 0-.53l.53-.53a.37.37 0 0 1 .53 0l1.48 1.48 5.77-5.77-1.68-1.68a2.12 2.12 0 0 0-3 0L3.64 9.45a2.12 2.12 0 0 0 0 3l5.91 5.91a2.12 2.12 0 0 0 3 0l6.01-6.01a2.12 2.12 0 0 0 0-3l-.71-.71a.37.37 0 0 1 0-.53l.53-.53a.37.37 0 0 1 .53 0l.71.71a3.49 3.49 0 0 1 0 4.94l-6.01 6.01a3.49 3.49 0 0 1-4.94 0l-5.91-5.91a3.49 3.49 0 0 1-.15-4.81Z" />
                    </svg>
                  </Link>
                </div>
              </div>

              {/* Explore */}
              <div className="space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-900">
                  Explore
                </h3>
                <ul className="space-y-2.5">
                  <li>
                    <Link
                      className="text-sm text-slate-500 transition-colors hover:text-indigo-600"
                      href="/docs"
                    >
                      Documentation
                    </Link>
                  </li>
                  <li>
                    <Link
                      className="text-sm text-slate-500 transition-colors hover:text-indigo-600"
                      href="/guides"
                    >
                      Guides
                    </Link>
                  </li>
                  <li>
                    <Link
                      className="text-sm text-slate-500 transition-colors hover:text-indigo-600"
                      href="/login"
                    >
                      Dashboard
                    </Link>
                  </li>
                  <li>
                    <Link
                      className="text-sm text-slate-500 transition-colors hover:text-indigo-600"
                      href="/careers"
                    >
                      Careers
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Community */}
              <div className="space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-900">
                  Community
                </h3>
                <ul className="space-y-2.5">
                  <li>
                    <Link
                      target="_blank"
                      prefetch={false}
                      className="text-sm text-slate-500 transition-colors hover:text-indigo-600"
                      href={siteConfig.links.github}
                    >
                      GitHub
                    </Link>
                  </li>
                  <li>
                    <Link
                      target="_blank"
                      prefetch={false}
                      className="text-sm text-slate-500 transition-colors hover:text-indigo-600"
                      href={siteConfig.links.buyMeACoffee}
                    >
                      Buy Me A Coffee
                    </Link>
                  </li>
                  <li>
                    <Link
                      target="_blank"
                      prefetch={false}
                      className="text-sm text-slate-500 transition-colors hover:text-indigo-600"
                      href={siteConfig.links.hashnode}
                    >
                      Hashnode Blog
                    </Link>
                  </li>
                  <li>
                    <Link
                      target="_blank"
                      prefetch={false}
                      className="text-sm text-slate-500 transition-colors hover:text-indigo-600"
                      href="https://internship.botbyte.in/"
                    >
                      Internships
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Featured On */}
              <div className="space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-900">
                  Featured On
                </h3>
                <ul className="space-y-2.5">
                  <li>
                    <Link
                      target="_blank"
                      prefetch={false}
                      className="text-sm text-slate-500 transition-colors hover:text-indigo-600"
                      href={siteConfig.links.productHunt}
                    >
                      Product Hunt
                    </Link>
                  </li>
                  <li>
                    <Link
                      target="_blank"
                      prefetch={false}
                      className="text-sm text-slate-500 transition-colors hover:text-indigo-600"
                      href={siteConfig.links.devhunt}
                    >
                      Devhunt
                    </Link>
                  </li>
                  <li>
                    <Link
                      target="_blank"
                      prefetch={false}
                      className="text-sm text-slate-500 transition-colors hover:text-indigo-600"
                      href={siteConfig.links.resourceFyi}
                    >
                      Resource.fyi
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </>
        )}

        {/* Bottom bar */}
        <div
          className={`flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-slate-200/60 ${!simpleFooter ? "mt-12" : "mt-0"} pt-6`}
        >
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Image
              src="/botbyte-logo.png"
              alt={siteConfig.name}
              width={180}
              height={80}
              className="h-16 w-auto object-contain sm:h-16"
              priority
            />
          </div>
          <div className="flex items-center gap-5 text-sm text-slate-400">
            <Link
              href="/docs/legal/terms"
              className="transition-colors hover:text-indigo-500"
            >
              Terms
            </Link>
            <Link
              href="/docs/legal/privacy"
              className="transition-colors hover:text-indigo-500"
            >
              Privacy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
